import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { webapp } from '@lib/analytics';
import type { webappTypes } from '@lib/analytics';

type PageCategory = webappTypes.PageProperties['pageCategory'];

// First path segment → analytics page category. Sections mirror the product nav grouping in
// the sidebar (src/components/sidebar/menu/useMenuItems.tsx) — keep the two in sync when routes
// move. Unmapped segments fall back to 'unknown'.
const SEGMENT_CATEGORY: Record<string, PageCategory> = {
  '': 'home',
  directory: 'directory',
  // Collect
  sources: 'collect',
  addSource: 'collect',
  addSourceV1: 'collect',
  destinations: 'collect',
  addDestination: 'collect',
  connections: 'activate',
  transformations: 'collect',
  libraries: 'collect',
  // Unify
  profiles: 'unify',
  'audiences-v2': 'unify',
  // Activate
  audiences: 'activate',
  audiencesSetup: 'activate',
  models: 'activate',
  modelsSetup: 'activate',
  dataGraph: 'activate',
  dataGraphAudiences: 'activate',
  // Govern
  trackingPlans: 'govern',
  dataCatalog: 'govern',
  dataMapper: 'govern',
  botManagement: 'govern',
  eventBlocking: 'govern',
  // Monitor
  healthDashboard: 'monitor',
  syncs: 'monitor',
  // Settings
  organization: 'settings',
  profile: 'settings',
  workspace: 'settings',
  invoices: 'settings',
  trialExpired: 'settings',
  // Onboarding
  getStarted: 'onboarding',
  setup: 'onboarding',
  // Auth
  login: 'auth',
  signup: 'auth',
  esignup: 'auth',
  elogin: 'auth',
  sso: 'auth',
  oauth: 'auth',
  authorize: 'auth',
  forgotPassword: 'auth',
  acceptInvite: 'auth',
  blendoSignup: 'auth',
  selectOrganization: 'auth',
  userSuspended: 'auth',
};

const getPageMeta = (pathname: string): webappTypes.PageProperties => {
  const [, segment = ''] = pathname.split('/');
  return { pageName: segment || 'home', pageCategory: SEGMENT_CATEGORY[segment] ?? 'unknown' };
};

/**
 * Fires a `page` event on every SPA route change. The analytics snippet only fires `page()`
 * once on hard load, so client-side navigations were previously untracked.
 */
export const usePageTracking = () => {
  const { pathname } = useLocation();
  const isInitialRender = useRef(true);

  useEffect(() => {
    // The snippet already fired page() for the initial load — skip the first effect run.
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    webapp.page(getPageMeta(pathname));
  }, [pathname]);
};
